import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSystemApiModuleList(
    $filter: systemApiModuleFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSystemApiModuleList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        id
        name
        uiModuleComponentList {
          id
        }
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.SystemApiModuleListState = {
  result: null,
  error: null,
  loading: false
};

export const getSystemApiModuleListRequest = createAsyncThunk<
  TYPES.SystemApiModuleList,
  {
    filter?: TYPES.SystemApiModuleFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'system_api_module/get_system_api_module_list',
  async (
  params: {
    filter?: TYPES.SystemApiModuleFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSystemApiModuleList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSystemApiModuleListSlice = createSlice({
  name: 'get_system_api_module_list',
  initialState: initialState,
  reducers: {
    getSystemApiModuleListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSystemApiModuleListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getSystemApiModuleListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(getSystemApiModuleListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSystemApiModuleListClear } =
getSystemApiModuleListSlice.actions;

export const getSystemApiModuleList = createSelector(
  (state: RootState) =>
  state.systemApiModule.getSystemApiModuleList.result,
  (state: RootState) =>
  state.systemApiModule.getSystemApiModuleList.error,
  (state: RootState) =>
  state.systemApiModule.getSystemApiModuleList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSystemApiModuleListSlice.reducer;